
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface ImageUrlInputProps {
  onImageSelected: (url: string) => void;
  disabled?: boolean;
}

export function ImageUrlInput({ onImageSelected, disabled }: ImageUrlInputProps) {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleAddImage = async () => {
    if (!imageUrl.trim()) {
      toast.error("Por favor, insira uma URL válida");
      return;
    }

    try {
      setLoading(true);
      
      // Test if the URL is valid and accessible
      const response = await fetch(imageUrl, { method: 'HEAD' });
      
      if (!response.ok) {
        throw new Error("Não foi possível acessar esta imagem");
      }
      
      // Check if it's an image by content type
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.startsWith('image/')) {
        throw new Error("O URL fornecido não é uma imagem");
      }
      
      setPreview(imageUrl);
      onImageSelected(imageUrl);
      toast.success("Imagem adicionada com sucesso!");
    } catch (error) {
      console.error("Error loading image:", error);
      toast.error("Erro ao carregar a imagem. Verifique se o URL é válido e acessível.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setImageUrl("");
    setPreview(null);
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <div className="space-y-2">
        <Label htmlFor="imageUrl">URL da imagem</Label>
        <div className="flex gap-2">
          <Input
            id="imageUrl"
            placeholder="https://exemplo.com/imagem.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            disabled={disabled || loading}
          />
          <Button 
            onClick={handleAddImage} 
            disabled={disabled || loading || !imageUrl.trim()} 
            type="button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {preview && (
        <div className="relative">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-auto max-h-48 object-contain rounded-md" 
          />
          <Button
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handleRemove}
          >
            Remover
          </Button>
        </div>
      )}
    </div>
  );
}
